import { uniq } from 'lodash';
import React, { useState } from 'react';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ModalAddFuelBatch from './ModalAddFuelBatch';
import ModalEditContainerTank from './ModalEditContainerTank';
import ModalAdjustmentTransaction from './ModalAdjustmentTransaction';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { useMessage } from 'app/shared-components/MessageUtils';
import { useHistory } from 'react-router-dom';
import { formatNumber, threeDecimalformatCurrency } from '../../../utils/numberFormatting';
import { useTranslation } from 'react-i18next';
/**
 * Visual component of the fuel tank.
 * Props:
 *   batchHeights: Array<{id, gallons, color, percent}>
 *   tankCapacity: number
 */
const FuelTank = ({ batchHeights, tankCapacity, tankLocation, tankNotes, tankKindOfContent, inventoryValue, onAddPurchase, onAdded, tankIdentifier, tankData, compacctnum, showAdjustmentButton }) => {
  const { t } = useTranslation('navigation');
  const [showTooltip, setShowTooltip] = useState(false);
  const [openAddBatch, setOpenAddBatch] = useState(false);
  const [openEditTank, setOpenEditTank] = useState(false);
  const [openAdjustmentTransaction, setOpenAdjustmentTransaction] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, direction: 'right' });
  const showMessage = useMessage();
  const history = useHistory();
  const colors = ['#24668d', '#2196f3', '#ff9800', '#f44336','#9c27b0', '#3f51b5', '#009688', '#4caf50'];
  const uniqueColors = [];
  const currentInventory = batchHeights && batchHeights.length > 0 
    ? Math.fround(batchHeights.reduce((acc, batch) => acc + Math.fround(batch.quantity || batch.fuel_qty || 0), 0))
    : 0;
  const tankPercentage = ((currentInventory / tankCapacity) * 100).toFixed(2);

  const calculateTooltipPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 200; // Approximate height of the tooltip
    const margin = 15;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate position to the right of the element
    const rightPosition = rect.right + margin;
    const leftPosition = rect.left - tooltipWidth - margin;
    let top = rect.top + (rect.height / 2) - 175; // Centrar verticalmente
    
    // Check if there is enough space on the right
    const hasSpaceOnRight = rightPosition + tooltipWidth <= viewportWidth;
    
    // Check if there is enough space on the left
    const hasSpaceOnLeft = leftPosition >= 0;
    
    // Adjust vertical position if it goes off screen
    if (top < 0) {
      top = 10; // Margin from top
    } else if (top + tooltipHeight > viewportHeight) {
      top = viewportHeight - tooltipHeight - 10; // Margin from bottom
    }
    
    // Determine the best position (right by default, left if no space)
    let finalLeft, direction;
    if (hasSpaceOnRight) {
      finalLeft = rightPosition;
      direction = 'right';
    } else if (hasSpaceOnLeft) {
      finalLeft = leftPosition;
      direction = 'left';
    } else {
      // If there is no space on either side, center horizontally
      finalLeft = (viewportWidth - tooltipWidth) / 2;
      direction = 'right';
    }
    
    setTooltipPosition({ 
      top, 
      left: finalLeft,
      direction
    });
  };

  const handleAddFuelPurchase = () => {
    setOpenAddBatch(true);
  };

  const handleEditTank = () => {
    setOpenEditTank(true);
  };

  const handleAdjustmentTransaction = () => {
    setOpenAdjustmentTransaction(true);
  };

  const handleBatchClick = (batchId) => {
    // Navigate to transactions view with batch_id as a query parameter
    history.push(`/fuel-inventory/transactions?batch_id=${batchId}`);
  };

  function getColor(batchId) {
    const index = colors[uniqueColors.length];
    uniqueColors.push(batchId);
    if (uniqueColors.length >= colors.length) {
      // If all colors have been used, reset the index
      uniqueColors.length = 0;
      return colors[0];
    }
    return index;
  }

  if (!batchHeights || batchHeights.length === 0) {
    return (
      <div
        className="relative mx-0 flex flex-col items-left w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl min-w-[155px] lg:min-w-[255px]"
      >
        {/* Tank label */}
        <label className="text-15 font-semibold mb-2">{tankLocation}</label>
        <div
          className="relative flex flex-col-reverse overflow-hidden items-center justify-center w-full"
          style={{
            height: '35vw',
            minHeight: 250,
            maxHeight: 350,
            minWidth: '155px',
            maxWidth: '225px',
            borderRadius: '2rem',
            border: '4px solid #d1d5db',
            background: '#f3f4f6',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            calculateTooltipPosition(e);
            setShowTooltip(true);
          }}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* + Button to add purchase - inside the tank */}
          {onAddPurchase && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={handleAddFuelPurchase}
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  minWidth: 'auto', 
                  px: 1, 
                  py: 0.5,
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  zIndex: 9999,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <AddIcon fontSize="small" />
              </Button>
              {showAdjustmentButton && (
                <Button
                  variant="contained"
                  color="info"
                  onClick={handleAdjustmentTransaction}
                  sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 48,
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5,
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    zIndex: 9999,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </Button>
              )}
            </>
          )}
          
          {/* Edit Button - only visible in Fuel and Lube Dashboard (when onAddPurchase is not provided) */}
          {!onAddPurchase && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditTank}
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  minWidth: 'auto', 
                  px: 1, 
                  py: 0.5,
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  zIndex: 9999,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <EditIcon fontSize="small" />
              </Button>
              {showAdjustmentButton && (
                <Button
                  variant="contained"
                  color="info"
                  onClick={handleAdjustmentTransaction}
                  sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 48,
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5,
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    zIndex: 9999,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </Button>
              )}
            </>
          )}
          <span className="text-gray-500 font-semibold">{t('NO_BATCHES_ADDED_YET')}</span>
          
          {/* Tooltip */}
          {showTooltip && (
            <div
              className="fixed z-50 p-4 rounded-lg bg-gray-900 text-white shadow-2xl border border-gray-700"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                width: '320px',
                maxWidth: '320px',
                pointerEvents: 'none',
                animation: 'fadeIn 0.2s ease-in-out',
              }}
            >
              {/* Tooltip arrow */}
              <div
                className={`absolute top-1/2 transform -translate-y-1/2 ${
                  tooltipPosition.direction === 'right' 
                    ? 'left-0 -translate-x-full' 
                    : 'right-0 translate-x-full'
                }`}
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  [tooltipPosition.direction === 'right' ? 'borderRight' : 'borderLeft']: '8px solid #374151',
                }}
              />
              <h3 className="text-lg font-semibold mb-3 text-blue-300 border-b border-gray-600 pb-2">{t('TANK_DETAILS')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap">
                  <span className="font-semibold text-gray-200">{t('TANK_NICKNAME')}</span>
                  <span className="text-white ml-auto">{tankLocation || 'N/A'}</span>
                </div>
                <Divider />
                <div className="flex flex-wrap">
                  <span className="font-semibold text-gray-200">{t('CONTENT_LABEL')}</span>
                  <span className="text-white ml-auto">{tankKindOfContent || 'N/A'}</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold text-gray-200">{t('CAPACITY_LABEL')}</span>
                  <span className="text-white ml-auto">{formatNumber(tankCapacity)} units</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold text-gray-200">{t('PERCENTAGE_LABEL')}</span>
                  <span className="text-white ml-auto">{formatNumber(tankPercentage)}%</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold text-gray-200">{t('INVENTORY_LABEL')}</span>
                  <span className="text-white ml-auto">{formatNumber(currentInventory)} units</span>
                </div>
                <div className="flex flex-wrap">
                  <span className="font-semibold text-gray-200">{t('INVENTORY_VALUE_LABEL')}</span>
                  <span className="text-white ml-auto">${threeDecimalformatCurrency(inventoryValue)}</span>
              </div>
                {tankNotes && (
                  <div className="pt-2 border-t border-gray-600">
                    <span className="font-semibold text-gray-200">{t('NOTES_LABEL')}</span>
                    <div className="text-white text-xs mt-1">{tankNotes}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <ModalAddFuelBatch open={openAddBatch} setOpen={setOpenAddBatch} onAdded={onAdded} containerTankId={tankIdentifier} />
        <ModalEditContainerTank 
          open={openEditTank} 
          setOpen={setOpenEditTank} 
          tankData={tankData} 
          onUpdated={onAdded}
          compacctnum={compacctnum}
        />
        <ModalAdjustmentTransaction 
          open={openAdjustmentTransaction} 
          setOpen={setOpenAdjustmentTransaction} 
          containerTankId={tankIdentifier}
          tankNickname={tankLocation}
          tankData={tankData}
          onAdded={onAdded}
        />
      </div>
    );
  }

  // Renderizar el tanque con los lotes
  return (
    <div
      className="relative mx-0 flex flex-col items-left w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl min-w-[155px] lg:min-w-[255px]"
    >
      {/* Tank label */}
      <label className="text-15 font-semibold mb-2">{tankLocation}</label>
      <div
        className="relative overflow-hidden flex flex-col items-center justify-end w-full"
        style={{
          height: '35vw',
          minHeight: 250,
          maxHeight: 350,
          minWidth: '155px',
          maxWidth: '225px',
          borderRadius: '2rem',
          border: '4px solid #d1d5db',
          background: '#f3f4f6',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          calculateTooltipPosition(e);
          setShowTooltip(true);
        }}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* + Button to add purchase - inside the tank */}
        {onAddPurchase && (
          <>
            <Button
              variant="contained"
              color="success"
              onClick={handleAddFuelPurchase}
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                minWidth: 'auto', 
                px: 1, 
                py: 0.5,
                borderRadius: '50%',
                width: 32,
                height: 32,
                zIndex: 9999,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              <AddIcon fontSize="small" />
            </Button>
            {showAdjustmentButton && (
              <Button
                variant="contained"
                color="info"
                onClick={handleAdjustmentTransaction}
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 48,
                  minWidth: 'auto', 
                  px: 1, 
                  py: 0.5,
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  zIndex: 9999,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <SettingsIcon fontSize="small" />
              </Button>
            )}
          </>
        )}
        
        {/* Edit Button - only visible in Fuel and Lube Dashboard (when onAddPurchase is not provided) */}
        {!onAddPurchase && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditTank}
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                minWidth: 'auto', 
                px: 1, 
                py: 0.5,
                borderRadius: '50%',
                width: 32,
                height: 32,
                zIndex: 9999,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              <EditIcon fontSize="small" />
            </Button>
            {showAdjustmentButton && (
              <Button
                variant="contained"
                color="info"
                onClick={handleAdjustmentTransaction}
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 48,
                  minWidth: 'auto', 
                  px: 1, 
                  py: 0.5,
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  zIndex: 9999,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <SettingsIcon fontSize="small" />
              </Button>
            )}
          </>
        )}
        {[...batchHeights].reverse().map((batch, idx) => (
            <div
              key={batch.id}
              style={{
                background: getColor(batch.id),
                width: '100%',
                height: `${((batch.quantity || batch.fuel_qty || 0) / tankCapacity) * 100}%`,
                borderRadius: idx === 0 ? '2rem 2rem 0 0' : 0,
                transition: 'height 0.5s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                position: 'relative',
                cursor: 'pointer',
              }}
              aria-label={`Color of batch ${batch.id}`}
              onClick={() => handleBatchClick(batch.batch_id)}
            >
            <span style={{
              position: 'absolute',
              left: 10,
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              textShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }}>
              {formatNumber(batch.quantity || batch.fuel_qty || 0)} {t('UNITS')}
            </span>
            <span style={{
              position: 'absolute',
              marginTop: 27.5,
              left: 10,
              color: '#fff',
              fontWeight: 450,
              fontSize: 9,
              textShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }}>
              {t('BATCH_ID_LABEL')} {batch.batch_id}
            </span>
          </div>
        ))}
        
        {/* Tooltip */}
        {showTooltip && (
          <div
            className="fixed z-50 p-4 rounded-lg bg-gray-900 text-white shadow-2xl border border-gray-700"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              width: '320px',
              maxWidth: '320px',
              pointerEvents: 'none',
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          >
            {/* Tooltip arrow */}
            <div
              className={`absolute top-1/2 transform -translate-y-1/2 ${
                tooltipPosition.direction === 'right' 
                  ? 'left-0 -translate-x-full' 
                  : 'right-0 translate-x-full'
              }`}
              style={{
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                [tooltipPosition.direction === 'right' ? 'borderRight' : 'borderLeft']: '8px solid #374151',
              }}
            />
            <h3 className="text-lg font-semibold mb-3 text-blue-300 border-b border-gray-600 pb-2">{t('TANK_DETAILS')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap">
                <span className="font-semibold text-gray-200">{t('TANK_NICKNAME')}</span>
                <span className="text-white ml-auto">{tankLocation || 'N/A'}</span>
              </div>
              <div className="flex flex-wrap">
                <span className="font-semibold text-gray-200">{t('CONTENT_LABEL')}</span>
                <span className="text-white ml-auto">{tankKindOfContent || 'N/A'}</span>
              </div>
              <div className="flex flex-wrap">
                <span className="font-semibold text-gray-200">{t('CAPACITY_LABEL')}</span>
                <span className="text-white ml-auto">{formatNumber(tankCapacity)} units</span>
              </div>
              <div className="flex flex-wrap">
                <span className="font-semibold text-gray-200">{t('PERCENTAGE_LABEL')}</span>
                <span className="text-white ml-auto">{formatNumber(tankPercentage)}%</span>
              </div>
              <div className="flex flex-wrap">
                <span className="font-semibold text-gray-200">{t('INVENTORY_LABEL')}</span>
                <span className="text-white ml-auto">{formatNumber(currentInventory)} units</span>
              </div>
              <div className="flex flex-wrap">
                <span className="font-semibold text-gray-200">{t('INVENTORY_VALUE_LABEL')}</span>
                <span className="text-white ml-auto">${threeDecimalformatCurrency(inventoryValue)}</span>
              </div>
              {tankNotes && (
                <div className="pt-2 border-t border-gray-600">
                  <span className="font-semibold text-gray-200">{t('NOTES_LABEL')}</span>
                  <div className="text-white text-xs mt-1">{tankNotes}</div>
                </div>
              )}
            </div>
          </div>
        )}
        <ModalAddFuelBatch open={openAddBatch} setOpen={setOpenAddBatch} onAdded={onAdded} containerTankId={tankIdentifier} />
        <ModalEditContainerTank 
          open={openEditTank} 
          setOpen={setOpenEditTank} 
          tankData={tankData} 
          onUpdated={onAdded}
          compacctnum={compacctnum}
        />
        <ModalAdjustmentTransaction 
          open={openAdjustmentTransaction} 
          setOpen={setOpenAdjustmentTransaction} 
          containerTankId={tankIdentifier}
          tankNickname={tankLocation}
          tankData={tankData}
          onAdded={onAdded}
        />
      </div>
    </div>
  );
};

export default FuelTank;
